<template>
  <a-form
    ref="form"
    :hide-required-mark="true"
    class="form"
    :model="formModel"
    layout="vertical"
    @finish="onFinish"
  >
    <a-form-item
      :label="$t('1-nin-shu-yu')"
      name="usedFor"
      :rules="[{ required: true, message: $t('qing-xuan-ze') }]"
    >
      <a-radio-group v-model:value="formModel.usedFor" :options="usedForOption()" />
    </a-form-item>
    <a-form-item
      v-if="formModel.usedFor === TEXT_OTHER"
      name="usedForOther"
      :rules="[{ required: true, message: $t('qing-shu-ru') }]"
    >
      <a-input
        v-model:value="formModel.usedForOther"
        :placeholder="$t('qing-shu-ru-qi-ta-yong-tu')"
      />
    </a-form-item>
    <a-form-item
      :label="$t('2-nin-jie-chu-shi-pin-zhi-zuo-duo-jiu')"
      name="contactDuration"
      :rules="[
        {
          required: true,
          message: $t('qing-xuan-ze-jie-chu-shi-pin-zhi-zuo-shi-chang'),
        },
      ]"
    >
      <a-radio-group v-model:value="formModel.contactDuration" :options="contactDurationOption()" />
    </a-form-item>
    <a-form-item
      :label="$t('3-nin-chuang-zuo-de-shi-pin-shi-chang-yi-ban-shi')"
      name="createDuration"
      :rules="[{ required: true, message: $t('qing-xuan-ze-chuang-zuo-shi-chang') }]"
    >
      <a-radio-group v-model:value="formModel.createDuration" :options="createDurationOption()" />
    </a-form-item>
    <a-form-item
      :label="$t('4-chuang-zuo-shi-pin-de-pin-shuai-shi')"
      name="createFrequency"
      :rules="[{ required: true, message: $t('qing-xuan-ze-chuang-zuo-pin-shuai') }]"
    >
      <a-radio-group v-model:value="formModel.createFrequency" :options="createFrequencyOption()" />
    </a-form-item>
    <a-form-item
      name="workIndustry"
      :label="$t('5-xuan-ze-nin-de-gong-zuo-hang-ye')"
      :rules="[{ required: true, message: $t('qing-xuan-ze-hang-ye') }]"
    >
      <a-radio-group v-model:value="formModel.workIndustry" :options="workIndustryOption()" />
    </a-form-item>
    <a-form-item
      v-if="formModel.workIndustry === TEXT_OTHER"
      name="workIndustryOther"
      :rules="[{ required: true, message: $t('qing-shu-ru') }]"
    >
      <a-input
        v-model:value="formModel.workIndustryOther"
        :placeholder="$t('qing-shu-ru-qi-ta-hang-ye')"
      />
    </a-form-item>
    <a-form-item
      :label="$t('6-gan-xing-qu-de-ying-yong-chang-jing-duo-xuan')"
      name="interestScenarios"
      :class="{ 'mb-[16px]': formModel.interestScenarios.includes(TEXT_OTHER) }"
      :rules="[{ required: true, message: $t('qing-xuan-ze-ying-yong-chang-jing') }]"
    >
      <a-checkbox-group v-model:value="formModel.interestScenarios" :options="scenariosOptions()" />
    </a-form-item>
    <a-form-item
      v-if="formModel.interestScenarios.includes(TEXT_OTHER)"
      name="interestScenariosOther"
      :rules="[{ required: true, message: $t('qing-shu-ru') }]"
    >
      <a-input
        v-model:value="formModel.interestScenariosOther"
        :placeholder="$t('qing-shu-ru-qi-ta-ying-yong-chang-jing')"
      />
    </a-form-item>
    <a-form-item
      name="email"
      :label="$t('7-you-xiang')"
      :rules="[{ required: true, message: $t('qing-shu-ru-you-xiang-di-zhi') }]"
    >
      <a-input
        v-model:value="formModel.email"
        class="w-[314px]"
        :placeholder="$t('qing-shu-ru-you-xiang-di-zhi')"
        :maxlength="60"
      />
    </a-form-item>
    <a-form-item name="wechatNum" label="$t('8-wei-xin-hao-xuan-tian')">
      <a-input
        v-model:value="formModel.wechatNum"
        class="w-[314px]"
        :placeholder="$t('qing-shu-ru-wei-xin-hao')"
        :maxlength="20"
      />
    </a-form-item>
    <div class="pt-[20px]">
      <a-button class="w-[314px]" type="primary" html-type="submit" :loading="loading">
        {{ $t('ti-jiao-shen-he') }}
      </a-button>
    </div>
  </a-form>
</template>
<script lang="ts" setup>
import type { FormInstance } from 'ant-design-vue';
import {
  usedForOption,
  createDurationOption,
  createFrequencyOption,
  workIndustryOption,
  contactDurationOption,
  scenariosOptions,
  TEXT_OTHER as getTextOther,
} from './constants';
import { applyYanbi } from '~/api';
import type { YanbiApplyResponse } from '~/api/interface';
import { useTaste } from '~/hooks/useTaste';

const TEXT_OTHER = getTextOther();

const emit = defineEmits({ submitSuccess: (data: YanbiApplyResponse) => true });

const form = ref<FormInstance>();
const loading = ref(false);

const formModel = reactive({
  usedFor: '',
  usedForOther: '',
  contactDuration: '',
  createDuration: '',
  createFrequency: '',
  workIndustry: '',
  workIndustryOther: '',
  interestScenarios: [] as string[],
  interestScenariosOther: '',
  wechatNum: '',
  email: '',
});

// const indexTags = useIndexTags()

// const scenariosOption = computed(() => {
//   return indexTags.value.tags.find((item) => item.tag_name === '应用场景') // 这里只能用中文去筛选，应为数据是可配的，包括key ~ ~
// })

// const industryOption = computed(() =>
//   indexTags.value.tags.find((item) => item.tag_name === '行业')
// )

async function onFinish(values: typeof formModel) {
  loading.value = true;
  try {
    const canApply = await useTaste();

    if (canApply.value) {
      const { data } = await applyYanbi(formModel);
      // todo: 根据结果处理
      emit('submitSuccess', data!);
    }
  } catch (error) {
    console.log(error);
  }
  loading.value = false;
}
</script>
<style lang="scss" scoped>
.form {
  :deep(.ant-form-item-label > label) {
    @apply text-font-1 font-semibold text-[16px];

    &::before {
      display: none;
    }
  }

  :deep(.ant-radio-wrapper) {
    margin-inline-end: 12px;
  }
}
</style>
